import {Injectable} from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import {filter, firstValueFrom, map, Observable, take} from 'rxjs';
import {AuthState} from '../../store/auth/reducers/auth.reducer';
import {Store} from '@ngrx/store';
import {selectAuthRelatedLoading, selectForAuthGuardRouting} from '../../store/auth/selectors/auth.selectors';
import {Roles} from '../../enum/roles';
import {Route} from '../../enum/route';
import {OAuthService} from 'angular-oauth2-oidc';
import {UserService} from '../user/user.service';
import {AuthService} from '../auth/auth.service';
import {LoadingService} from '../loadingService/loading.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard  {

  constructor(private readonly userService: UserService,
              private readonly oauthService: OAuthService,
              private readonly authService: AuthService,
              private readonly loadingService: LoadingService,
              private readonly router: Router,
              private readonly authStore: Store<AuthState>,
  ) {
  }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (!this.oauthService.hasValidAccessToken()) {
      this.router.navigateByUrl('/').then();
    }

    return firstValueFrom(this.authStore.select(selectAuthRelatedLoading)
      .pipe(filter(value => {
          return value.isAuthenticated && !value.userLoading && !value.companyLoading;
        }),
        map((value) => {
          if (value.roles.length === 0
            || (route.data.roles
              && value.roles.filter(value => route.data.roles.includes(value)).length === 0)) {
            this.router.navigateByUrl('/').then();
          }
          return value;
        })))
      .then((value) => {
          return this.performSituativeRouting(route, value);
        }
      );
  }

  private performSituativeRouting(route: ActivatedRouteSnapshot, value: { userLoading: boolean; companyLoading: boolean; roles: Roles[]; isAuthenticated: boolean }) {
    return firstValueFrom(this.authStore.select(selectForAuthGuardRouting).pipe(take(1))).then((result) => {
      if (result.companyCreated === undefined
        && (result.roles.includes(Roles.ROLE_CORPORATE_ADMIN) || result.roles.includes(Roles.ROLE_DIRECT_CORPORATE_ADMIN) || result.roles.includes(Roles.ROLE_REPRESENTATIVE))
        && !route.url.toString().includes(Route.CREATE_COMPANY)) {
        return this.router.navigateByUrl(Route.CREATE_COMPANY);
      }

      if (result.userCreated === undefined
        && !route.url.toString().includes(Route.CREATE_USER)
        && !result.roles.includes(Roles.ROLE_DIRECT_CORPORATE_ADMIN)) {
        return this.router.navigateByUrl(Route.CREATE_USER);
      }

      return value.isAuthenticated;
    });
  }
}
